import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { businessSelectors, businessTypes } from 'app-components/flux/business';
import type { BusinessColor } from 'app-components/utils/theme';
import { createFormHooks } from 'app-components/utils/createForm/formHooks';
import { getAddBusinessColors, getColorDisplayName } from 'components/Business/businessColorOptions';
import BusinessLogoField from 'components/Business/BusinessLogoField';
import FormSectionHeader from 'components/FormSectionHeader';
import InfoTooltipIcon from 'components/InfoTooltipIcon';
import QChoiceChips from 'components/QuickenControls/QChoiceChips';
import React, { useEffect, useMemo } from 'react';
import { useAppSelector } from 'hooks/redux';

import { businessFormFactory } from 'app-components/forms/business/businessForm';

export const { FormProvider, ...businessFormHooks } = createFormHooks(businessFormFactory);

type BusinessTextFieldProps = {
  fieldName:
    | 'name'
    | 'email'
    | 'phoneNumber'
    | 'industry'
    | 'structure'
    | 'line1'
    | 'line2'
    | 'zipCode'
    | 'city'
    | 'state';
  afterValueChange?: (value: string) => void;
} & Omit<TextFieldProps, 'value' | 'onChange' | 'error' | 'helperText'>;

function BusinessTextField({ fieldName, children, afterValueChange, ...rest }: BusinessTextFieldProps) {
  const { onValueChange, value, helperText, error } = businessFormHooks.useField(fieldName);
  return (
    <TextField
      {...rest}
      value={value ?? ''}
      helperText={helperText}
      error={error}
      onChange={(e) => {
        onValueChange(e.target.value);
        afterValueChange?.(e.target.value);
      }}
      autoComplete='off'
    >
      {children}
    </TextField>
  );
}

export const NameField = React.memo(function NameField() {
  return (
    <BusinessTextField
      id='business-name-field'
      data-testid='business-name-field'
      label='Business name'
      fieldName='name'
      variant='filled'
      fullWidth
      autoFocus
      required
    />
  );
});

export const EmailField = React.memo(function EmailField() {
  return (
    <BusinessTextField
      id='business-email-field'
      data-testid='business-email-field'
      label='Email'
      fieldName='email'
      variant='filled'
      fullWidth
      type='email'
      required
    />
  );
});

export const PhoneField = React.memo(function PhoneField() {
  return (
    <BusinessTextField
      id='business-phone-field'
      data-testid='business-phone-field'
      label='Phone number'
      fieldName='phoneNumber'
      variant='filled'
      fullWidth
    />
  );
});

export const IndustryField = React.memo(function IndustryField() {
  return (
    <BusinessTextField
      id='industry-select-element'
      data-testid='industry-select-element'
      label='Industry'
      fieldName='industry'
      select
      variant='filled'
      fullWidth
      required
    >
      {businessTypes.industryOptions.map(([key, label]) => (
        <MenuItem id={key} key={key} value={key}>
          {label}
        </MenuItem>
      ))}
    </BusinessTextField>
  );
});

type StructureFieldProps = {
  onChange?: (structure: keyof typeof businessTypes.businessStructureObj) => void;
};

export const StructureField = React.memo(function StructureField({ onChange }: StructureFieldProps) {
  return (
    <BusinessTextField
      id='business-structure-select-element'
      data-testid='business-structure-select-element'
      label='Structure'
      fieldName='structure'
      select
      variant='filled'
      fullWidth
      required
      afterValueChange={(value) => onChange?.(value as keyof typeof businessTypes.businessStructureObj)}
    >
      {Object.keys(businessTypes.businessStructureObj).map((key) => (
        <MenuItem key={key} value={key}>
          {businessTypes.businessStructureObj[key as keyof typeof businessTypes.businessStructureObj]}
        </MenuItem>
      ))}
    </BusinessTextField>
  );
});

export const ColorField = React.memo(function ColorField() {
  const theme = useTheme();
  const { onValueChange, value, helperText, error } = businessFormHooks.useField('color');
  const availableColors = useAppSelector(businessSelectors.getBusinessColors);
  const colors: ReadonlyArray<BusinessColor> = getAddBusinessColors(availableColors, value as BusinessColor);

  return (
    <Stack gap={2}>
      <FormSectionHeader
        title='Color'
        caption='Helps identify your business across Quicken Business & Personal'
        divider
      />
      <TextField
        id='color-select-element'
        label='Select'
        variant='filled'
        fullWidth
        select
        required
        value={(value as string) ?? ''}
        helperText={helperText}
        error={error}
        onChange={(e) => onValueChange(e.target.value as BusinessColor)}
      >
        {colors.map((color) => (
          <MenuItem key={color} value={color} aria-label={getColorDisplayName(color)}>
            <Box display='flex' alignItems='center' gap={1}>
              <Box
                // @ts-expect-error palette color key
                bgcolor={theme.palette[color]?.main || theme.palette.background.layer2}
                height={20}
                width={20}
                borderRadius='50%'
              />
              <Typography variant='paragraphRegular'>{getColorDisplayName(color)}</Typography>
            </Box>
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );
});

const COLOR_SWATCH_SIZE = 24;

type ColorSwatchProps = {
  color: BusinessColor;
  selected?: boolean;
};

function ColorSwatch({ color, selected }: ColorSwatchProps) {
  const theme = useTheme();
  return (
    <Box
      width={COLOR_SWATCH_SIZE}
      height={COLOR_SWATCH_SIZE}
      borderRadius='50%'
      padding={selected ? '3px' : 0}
      border={selected ? 1 : 0}
      borderColor='primary.main'
    >
      <Box
        width='100%'
        height='100%'
        borderRadius='50%'
        // @ts-expect-error palette color key
        bgcolor={theme.palette[color]?.main || theme.palette.background.layer2}
      />
    </Box>
  );
}

export const ColorPickerField = React.memo(function ColorPickerField() {
  const { onValueChange, value, helperText, error } = businessFormHooks.useField('color');
  const availableColors = useAppSelector(businessSelectors.getBusinessColors);
  const colors: ReadonlyArray<BusinessColor> = getAddBusinessColors(availableColors, value as BusinessColor);

  return (
    <Stack gap={2}>
      <FormSectionHeader title='Color' caption='Helps identify your business across Quicken Business & Personal' />
      <FormControl error={error}>
        <RadioGroup
          row
          name='business-color'
          aria-label='Color'
          value={(value as string) ?? ''}
          onChange={(event) => onValueChange(event.target.value as BusinessColor)}
          sx={{ gap: 0.5 }}
        >
          {colors.map((color) => (
            <Tooltip key={color} title={getColorDisplayName(color)} placement='top' arrow>
              <Radio
                id={`business-color-${color}`}
                value={color}
                disableRipple
                icon={<ColorSwatch color={color} />}
                checkedIcon={<ColorSwatch color={color} selected />}
                inputProps={{ 'aria-label': getColorDisplayName(color) }}
                sx={{ padding: 0 }}
              />
            </Tooltip>
          ))}
        </RadioGroup>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    </Stack>
  );
});

export const LogoField = React.memo(function LogoField() {
  const { onValueChange, value } = businessFormHooks.useField('logoId');
  return (
    <Stack gap={2}>
      <FormSectionHeader title='Logo' optional divider />
      <BusinessLogoField initialLogoId={(value as string) || undefined} onSelect={(docId) => onValueChange(docId)} />
      <Stack gap={0.5}>
        <Typography variant='captionRegular' color='text.secondary'>
          {'Logo will display on documents you send to clients.'}
        </Typography>
        <Typography variant='captionRegular' color='text.secondary'>
          {'Upload an image file (PNG or SVG preferred) that is less than 2MB and 100 pixels tall.'}
        </Typography>
      </Stack>
    </Stack>
  );
});

const ACCOUNTING_OPTIONS = [
  { label: 'Cash basis', value: 'CASH' },
  { label: 'Accrual basis', value: 'ACCRUAL' },
] as const;

const ACCOUNTING_TOOLTIP = (
  <span>
    <b>Cash basis (default)</b>
    <br />
    Records revenue when cash is received and expenses when cash is paid out.
    <br />
    <b>Accrual basis</b>
    <br />
    Records revenue and expenses only when services have been rendered, even if unpaid.
  </span>
);

type AccountingTypeFieldProps = {
  divider?: boolean;
  infoPlacement?: 'title' | 'caption';
};

export const AccountingTypeField = React.memo(function AccountingTypeField({
  divider = true,
  infoPlacement = 'title',
}: AccountingTypeFieldProps) {
  const { onValueChange, value } = businessFormHooks.useField('accountingType');
  const infoIcon = (
    <InfoTooltipIcon
      tooltipHTML={ACCOUNTING_TOOLTIP}
      size={infoPlacement === 'caption' ? 'small-no-padding' : 'medium'}
      aria-label='Accounting method information'
      {...(infoPlacement === 'caption' && {
        icon: InfoOutlinedIcon,
        IconProps: { style: { width: 16, height: 16 } },
      })}
    />
  );
  return (
    <Stack gap={2}>
      <FormSectionHeader
        title='Accounting method'
        caption='Cash basis is set as a default method.'
        divider={divider}
        endAdornment={infoPlacement === 'title' ? infoIcon : undefined}
        captionEndAdornment={infoPlacement === 'caption' ? infoIcon : undefined}
      />
      <QChoiceChips
        items={ACCOUNTING_OPTIONS as unknown as (typeof ACCOUNTING_OPTIONS)[number][]}
        labelFromItem={(item: (typeof ACCOUNTING_OPTIONS)[number]) => item.label}
        keyFromItem={(item: (typeof ACCOUNTING_OPTIONS)[number]) => item.value}
        selectedItems={[value === 'ACCRUAL' ? ACCOUNTING_OPTIONS[1] : ACCOUNTING_OPTIONS[0]]}
        onSelected={(val: (typeof ACCOUNTING_OPTIONS)[number][]) => {
          onValueChange(val[0].value);
        }}
      />
    </Stack>
  );
});

type TaxGroupFieldProps = {
  inferredTaxGroup?: string;
  onChange?: (taxGroup: keyof typeof businessTypes.taxGroupNames) => void;
  fieldMaxWidth?: number;
};

export const TaxGroupField = React.memo(function TaxGroupField({
  inferredTaxGroup,
  onChange,
  fieldMaxWidth,
}: TaxGroupFieldProps) {
  const { onValueChange, value, helperText, error } = businessFormHooks.useField('taxGroup');
  const { value: structureValue } = businessFormHooks.useField('structure');

  const taxGroupHelperText = useMemo(() => {
    if (!inferredTaxGroup) return '';
    const inferredFormName = businessTypes.taxGroupNames[inferredTaxGroup as keyof typeof businessTypes.taxGroupNames];
    if (!inferredFormName) return '';
    return `Based on your business details, the most common form filed annually with the IRS is ${inferredFormName}. You can change later on in settings.`;
  }, [inferredTaxGroup]);

  const shouldShowTaxGroup = structureValue !== 'C_CORP';
  if (!shouldShowTaxGroup) return null;

  return (
    <Stack gap={2}>
      <FormSectionHeader title='Tax group' caption={taxGroupHelperText || undefined} />
      <TextField
        id='tax-group-select'
        data-testid='tax-group-select'
        label='Tax group'
        variant='filled'
        fullWidth
        sx={{ maxWidth: fieldMaxWidth }}
        select
        value={value ?? ''}
        helperText={helperText}
        error={error}
        onChange={(e) => {
          const taxGroup = e.target.value as keyof typeof businessTypes.taxGroupNames;
          onValueChange(taxGroup);
          onChange?.(taxGroup);
        }}
        autoComplete='off'
      >
        {Object.keys(businessTypes.taxGroupNames).map((key) => (
          <MenuItem key={key} value={key}>
            {businessTypes.taxGroupCategoryNames[key as keyof typeof businessTypes.taxGroupCategoryNames]} (i.e.{' '}
            {businessTypes.taxGroupNames[key as keyof typeof businessTypes.taxGroupNames]})
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );
});

export const AddressSection = React.memo(function AddressSection() {
  const { value: line1 } = businessFormHooks.useField('line1');
  const { value: line2 } = businessFormHooks.useField('line2');
  const { value: city } = businessFormHooks.useField('city');
  const { value: state } = businessFormHooks.useField('state');
  const { value: zip } = businessFormHooks.useField('zipCode');
  const { onValueChange: setAddressEnabled } = businessFormHooks.useField('addressEnabled');

  const hasAddress = !!(line1 || line2 || city || state || zip);
  useEffect(() => {
    setAddressEnabled(hasAddress);
  }, [hasAddress, setAddressEnabled]);

  return (
    <Stack gap={2.5}>
      <BusinessTextField
        id='street-address-field'
        data-testid='street-address-field'
        label='Street address'
        fieldName='line1'
        variant='filled'
        fullWidth
      />
      <BusinessTextField
        id='street-address-line2-field'
        data-testid='street-address-line2-field'
        label='Address line 2'
        fieldName='line2'
        variant='filled'
        fullWidth
      />
      <BusinessTextField
        id='city-field'
        data-testid='city-field'
        label='City'
        fieldName='city'
        variant='filled'
        fullWidth
      />
      <Stack direction='row' gap={1.5}>
        <BusinessTextField
          id='state-field'
          data-testid='state-field'
          label='State'
          fieldName='state'
          variant='filled'
          fullWidth
          select
        >
          <MenuItem value=''>
            <em>None</em>
          </MenuItem>
          {businessTypes.usStatesArray.map((s: string) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </BusinessTextField>
        <BusinessTextField
          id='zip-field'
          data-testid='zip-field'
          label='ZIP'
          fieldName='zipCode'
          variant='filled'
          fullWidth
        />
      </Stack>
    </Stack>
  );
});

const CCorpWarning = React.memo(function CCorpWarning() {
  const { value: structureValue } = businessFormHooks.useField('structure');
  if (structureValue !== 'C_CORP') return null;
  return (
    <Alert severity='warning' sx={{ alignItems: 'flex-start' }}>
      <Typography variant='paragraphMedium' display='block' gutterBottom>
        Unsupported business structure
      </Typography>
      <Typography variant='captionRegular'>
        Simplifi does not yet support <b>C Corp</b> businesses. You may still add this business, but you will need to
        create and associate categories to the appropriate tax lines in order to create tax reports.
      </Typography>
    </Alert>
  );
});

export const BusinessDetailsSection = React.memo(function BusinessDetailsSection() {
  return (
    <Stack gap={2.5}>
      <NameField />
      <EmailField />
      <Stack direction='row' gap={1.5}>
        <IndustryField />
        <StructureField />
      </Stack>
      <CCorpWarning />
      <AccountingTypeField />
      <ColorField />
      <LogoField />
    </Stack>
  );
});

export const BusinessContactSection = React.memo(function BusinessContactSection({
  inferredTaxGroup,
}: {
  inferredTaxGroup?: string;
}) {
  const { value: structureValue } = businessFormHooks.useField('structure');
  const showTaxGroup = structureValue !== 'C_CORP';

  return (
    <Stack gap={2.5}>
      <TaxGroupField inferredTaxGroup={inferredTaxGroup} />
      <FormSectionHeader title='Contact' optional divider={showTaxGroup} />
      <PhoneField />
      <AddressSection />
    </Stack>
  );
});
